/**
 * Pedido de credencial para liberar operação restrita.
 *
 * Quem libera não é o operador do caixa: é alguém com acesso, que digita e vai
 * embora. Por isso os campos nascem vazios e nada fica guardado depois.
 */

import React, {useState} from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  Text,
  TextInput,
  View,
} from 'react-native';
import {Lock} from 'lucide-react-native';

import {ErroApi} from '../api/cliente';
import {autorizar} from '../autorizacao';
import {txt, useIdioma} from './idioma';
import {estilos, fontes, LARGURA_DA_COLUNA, useTema} from './tema';

export interface PropsDialogoSenha {
  visivel: boolean;
  titulo: string;
  descricao: string;
  aoFechar: () => void;
  aoLiberar: (nome?: string, offline?: boolean) => void;
}

export function DialogoSenha({
  visivel,
  titulo,
  descricao,
  aoFechar,
  aoLiberar,
}: PropsDialogoSenha) {
  const t = useTema();
  useIdioma();

  const [usuario, setUsuario] = useState('');
  const [senha, setSenha] = useState('');
  const [verificando, setVerificando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  function limpar() {
    setUsuario('');
    setSenha('');
    setErro(null);
  }

  async function conferir() {
    setVerificando(true);
    setErro(null);
    try {
      const resposta = await autorizar(usuario.trim(), senha.trim());

      if (!resposta.autorizado) {
        setErro(resposta.motivo ?? txt('Sem acesso'));
        return;
      }

      limpar();
      aoLiberar(resposta.nome, resposta.offline);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : txt('Não foi possível conferir o acesso agora'));
    } finally {
      setVerificando(false);
    }
  }

  const podeConferir = usuario.trim().length > 0 && senha.length > 0 && !verificando;

  const campo = {
    backgroundColor: t.fundo,
    borderColor: t.borda,
    color: t.texto,
  };

  return (
    <Modal visible={visivel} transparent animationType="fade" onRequestClose={aoFechar}>
      <KeyboardAvoidingView
        style={{
          flex: 1,
          backgroundColor: t.escurecedorModal,
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20,
        }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        {/* No computador o diálogo acompanha a coluna das telas; esticado na
            janela inteira, os campos ficariam com um metro de largura para
            quatro dígitos. */}
        <View
          style={[
            estilos.cartao,
            {
              backgroundColor: t.superficie,
              borderColor: t.borda,
              gap: 12,
              padding: 20,
              width: '100%',
              maxWidth: LARGURA_DA_COLUNA,
            },
          ]}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 10}}>
            <Lock size={20} color={t.aviso} strokeWidth={2.2} />
            <Text style={{color: t.texto, fontSize: 20, fontFamily: fontes.extra, flex: 1}}>
              {titulo}
            </Text>
          </View>

          <Text style={[estilos.subtitulo, {color: t.textoFraco}]}>{descricao}</Text>

          <TextInput
            style={[estilos.campo, campo]}
            value={usuario}
            onChangeText={setUsuario}
            placeholder={txt('ID de quem libera')}
            placeholderTextColor={t.textoApagado}
            keyboardType="number-pad"
            autoCapitalize="none"
            autoCorrect={false}
          />
          <TextInput
            style={[estilos.campo, campo]}
            value={senha}
            onChangeText={setSenha}
            placeholder={txt('Senha')}
            placeholderTextColor={t.textoApagado}
            secureTextEntry
            // A senha de quem libera é a mesma credencial do login — o PIN,
            // que é numérico. Sem isto o celular subia o teclado de letras
            // para digitar quatro dígitos.
            keyboardType="number-pad"
            onSubmitEditing={() => podeConferir && conferir()}
          />

          {erro ? <Text style={[estilos.erro, {color: t.erro}]}>{erro}</Text> : null}

          <View style={{flexDirection: 'row', gap: 12}}>
            <Pressable
              onPress={() => {
                limpar();
                aoFechar();
              }}
              style={[estilos.botao, {flex: 1, borderWidth: 1, borderColor: t.borda}]}>
              <Text style={[estilos.textoBotao, {color: t.textoFraco, fontSize: 17}]}>
                {txt('Cancelar')}
              </Text>
            </Pressable>

            <Pressable
              onPress={conferir}
              disabled={!podeConferir}
              style={[
                estilos.botao,
                {flex: 1, backgroundColor: podeConferir ? t.primaria : t.superficieAlta},
              ]}>
              {verificando ? (
                <ActivityIndicator color={t.tintaPrimaria} />
              ) : (
                <Text
                  style={[
                    estilos.textoBotao,
                    {fontSize: 17, color: podeConferir ? t.tintaPrimaria : t.textoApagado},
                  ]}>
                  {txt('Liberar')}
                </Text>
              )}
            </Pressable>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}
